import React, { useEffect, useState } from 'react'
import { FlatList, RefreshControl, Alert, Platform } from 'react-native'
import { ArrowLeft, ArrowRight, Calendar, User, MessageCircle, Check, X } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  Text,
  TopBar,
  XStack,
  YStack,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SpaceReservations'>
  route: RouteProp<RootStackParamList, 'SpaceReservations'>
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  price_total: number
  price_type: string
  status: string
  payment_status: string
  paid_amount: number
  seller_notes: string | null
  admin_notes: string | null
  created_at: string
  space: { id: string; name: string; space_type: string }
  seller: { id: string; email: string; full_name: string | null }
}

export default function SpaceReservationsScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all')

  useEffect(() => {
    fetchReservations()
  }, [marketId])

  const fetchReservations = async () => {
    try {
      const { data, error } = await supabase
        .from('space_reservations')
        .select(`
          id, start_date, end_date, price_total, price_type, status,
          payment_status, paid_amount, seller_notes, admin_notes, created_at, seller_id,
          space:market_spaces (id, name, space_type)
        `)
        .eq('flea_market_id', marketId)
        .order('created_at', { ascending: false })

      if (error) throw error

      const sellerIds = [...new Set((data || []).map((r: any) => r.seller_id).filter(Boolean))]
      let usersMap: Record<string, any> = {}

      if (sellerIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, display_name')
          .in('id', sellerIds)

        usersMap = (users || []).reduce((acc: any, u: any) => {
          acc[u.id] = {
            id: u.id,
            email: u.email,
            full_name: u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || null,
          }
          return acc
        }, {})
      }

      const transformed = (data || []).map((item: any) => ({
        ...item,
        space: Array.isArray(item.space) ? item.space[0] : item.space,
        seller: usersMap[item.seller_id] || null,
      }))
      setReservations(transformed)
    } catch (error) {
      console.error('Error fetching reservations:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = () => {
    setRefreshing(true)
    fetchReservations()
  }

  const updateReservationStatus = async (reservationId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('space_reservations')
        .update({ status: newStatus })
        .eq('id', reservationId)

      if (error) throw error

      setReservations((prev) =>
        prev.map((r) => r.id === reservationId ? { ...r, status: newStatus } : r)
      )

      const msg = t('status_updated') || 'Status updated!'
      Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('success'), msg)
    } catch (error: any) {
      console.error('Update error:', error)
      const msg = error.message || t('update_failed') || 'Failed to update'
      Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error'), msg)
    }
  }

  const confirmAction = (reservationId: string, action: 'confirm' | 'cancel') => {
    const newStatus = action === 'confirm' ? 'confirmed' : 'cancelled'
    const message = action === 'confirm'
      ? (t('confirm_reservation') || 'Confirm this reservation?')
      : (t('cancel_reservation') || 'Cancel this reservation?')

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        updateReservationStatus(reservationId, newStatus)
      }
    } else {
      Alert.alert(
        t('confirm') || 'Confirm',
        message,
        [
          { text: t('cancel') || 'Cancel', style: 'cancel' },
          {
            text: action === 'confirm' ? (t('confirm') || 'Confirm') : (t('cancel_action') || 'Cancel'),
            style: action === 'cancel' ? 'destructive' : 'default',
            onPress: () => updateReservationStatus(reservationId, newStatus),
          },
        ]
      )
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#4CAF50'
      case 'pending': return '#FF9800'
      case 'cancelled': return '#F44336'
      case 'completed': return '#2196F3'
      default: return '#999'
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#4CAF50'
      case 'partial': return '#FF9800'
      case 'unpaid': return '#F44336'
      case 'refunded': return '#9E9E9E'
      default: return '#999'
    }
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString()

  const filteredReservations = reservations.filter((r) => filter === 'all' || r.status === filter)

  const renderReservation = ({ item }: { item: Reservation }) => (
    <YStack backgroundColor="$backgroundCard" borderRadius="$md" padding="$md" marginBottom="$sm">
      {/* Header */}
      <XStack justifyContent="space-between" alignItems="flex-start" marginBottom="$sm">
        <YStack>
          <Text fontSize={18} fontWeight="600" color="$color">{item.space?.name || 'Unknown Space'}</Text>
          <Text fontSize={14} color="$colorSecondary" textTransform="capitalize">{item.space?.space_type}</Text>
        </YStack>
        <XStack paddingHorizontal={10} paddingVertical="$xs" borderRadius="$lg" backgroundColor={getStatusColor(item.status)}>
          <Text color="#fff" fontSize={12} fontWeight="600" textTransform="capitalize">{t(item.status) || item.status}</Text>
        </XStack>
      </XStack>

      {/* Seller */}
      <XStack alignItems="center" gap={6} marginBottom="$sm">
        <User size={14} color="#666" />
        <Text fontSize={14} color="$colorSecondary">{item.seller?.full_name || item.seller?.email || 'Unknown'}</Text>
      </XStack>

      {/* Dates */}
      <XStack alignItems="center" gap={6} marginBottom="$sm">
        <Calendar size={14} color="#666" />
        <Text fontSize={14} color="$color">{formatDate(item.start_date)}</Text>
        <ArrowRight size={14} color="#666" />
        <Text fontSize={14} color="$color">{formatDate(item.end_date)}</Text>
      </XStack>

      {/* Pricing */}
      <XStack justifyContent="space-between" alignItems="center" marginBottom="$sm">
        <Text fontSize={20} fontWeight="bold" color="#4CAF50">€{item.price_total.toFixed(2)}</Text>
        <XStack paddingHorizontal={10} paddingVertical="$xs" borderRadius="$lg" backgroundColor={getPaymentStatusColor(item.payment_status)}>
          <Text color="#fff" fontSize={12} fontWeight="600" textTransform="capitalize">
            {t(item.payment_status) || item.payment_status}
            {item.payment_status === 'partial' && ` (€${item.paid_amount})`}
          </Text>
        </XStack>
      </XStack>

      {/* Notes */}
      {item.seller_notes && (
        <XStack alignItems="flex-start" gap={6} marginTop="$sm">
          <MessageCircle size={14} color="#666" />
          <Text fontSize={14} color="$colorSecondary" fontStyle="italic">{item.seller_notes}</Text>
        </XStack>
      )}

      {/* Pending Actions */}
      {item.status === 'pending' && (
        <XStack gap="$sm" marginTop="$sm">
          <YStack
            f={1} backgroundColor="#4CAF50" padding="$sm" borderRadius="$md" alignItems="center"
            onPress={() => confirmAction(item.id, 'confirm')}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <XStack alignItems="center" gap="$xs">
              <Check size={16} color="#fff" />
              <Text color="#fff" fontWeight="600">{t('confirm') || 'Confirm'}</Text>
            </XStack>
          </YStack>
          <YStack
            f={1} backgroundColor="$backgroundCard" padding="$sm" borderRadius="$md" alignItems="center"
            borderWidth={1} borderColor="#F44336"
            onPress={() => confirmAction(item.id, 'cancel')}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <XStack alignItems="center" gap="$xs">
              <X size={16} color="#F44336" />
              <Text color="#F44336" fontWeight="600">{t('decline') || 'Decline'}</Text>
            </XStack>
          </YStack>
        </XStack>
      )}

      {/* Confirmed → Complete */}
      {item.status === 'confirmed' && (
        <YStack
          backgroundColor="#2196F3" padding="$sm" borderRadius="$md" alignItems="center" marginTop="$sm"
          onPress={() => updateReservationStatus(item.id, 'completed')}
          pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
        >
          <Text color="#fff" fontWeight="600">{t('mark_completed') || 'Mark Completed'}</Text>
        </YStack>
      )}
    </YStack>
  )

  const pendingCount = reservations.filter((r) => r.status === 'pending').length

  return (
    <YStack f={1} backgroundColor="$backgroundSecondary">
      {/* Header */}
      <XStack justifyContent="space-between" alignItems="center" paddingHorizontal="$md" paddingTop="$md" paddingBottom="$md" backgroundColor="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderColor">
        <XStack alignItems="center" gap="$xs" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <ArrowLeft size={18} color="#4A90A4" />
          <Text color="#4A90A4" fontSize={16}>{t('back') || 'Back'}</Text>
        </XStack>
        <Text fontSize={20} fontWeight="bold" color="$color">
          {t('reservations') || 'Reservations'}
          {pendingCount > 0 && ` (${pendingCount})`}
        </Text>
        <YStack width={50} />
      </XStack>

      {/* Filter Tabs */}
      <XStack backgroundColor="$backgroundCard" paddingHorizontal="$md" paddingVertical="$sm" borderBottomWidth={1} borderBottomColor="$borderColor">
        {(['all', 'pending', 'confirmed', 'cancelled'] as const).map((f) => (
          <YStack
            key={f} f={1} paddingVertical="$sm" alignItems="center" borderRadius="$md"
            backgroundColor={filter === f ? '#4A90A4' : 'transparent'}
            onPress={() => setFilter(f)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <Text fontSize={13} color={filter === f ? '#fff' : '$colorSecondary'} fontWeight={filter === f ? '600' : '400'}>
              {f === 'all' ? t('all') || 'All' : t(f) || f}
              {' '}({reservations.filter((r) => f === 'all' ? true : r.status === f).length})
            </Text>
          </YStack>
        ))}
      </XStack>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <FlatList
          data={filteredReservations}
          renderItem={renderReservation}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{ padding: 16, flexGrow: 1 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          ListEmptyComponent={
            <YStack f={1} jc="center" ai="center" paddingTop="$md">
              <Text color="$colorSecondary" fontSize={16}>{t('no_reservations') || 'No reservations yet'}</Text>
            </YStack>
          }
        />
      )}
    </YStack>
  )
}
